import React from 'react';
import pluralize from 'pluralize';
import { Typography } from '@saucelabs/ds-typography/lib';
import { amountSeparator, formatThousand } from '@saucelabs/sl-util-helpers/lib/numberHelpers';

import { PLAN_CODE_INTERNVAL, PLAN_INTERVAL, PRODUCT_API_TESTING, PRODUCT_LIVE, PRODUCT_MOBILE_QUALITY, PRODUCT_RDC, PRODUCT_USAGE, PRODUCT_VDC, TESTING_TYPES, TESTING_TYPE_TO_LABEL } from '../../../constants';
import { OrderSummarySeparator } from './OrderSummaryComponents';
import { projectsToMonthlyExecutions } from '../../../utils/plans';
import { PlanConfiguration } from '../../../records/PlanConfiguration';

interface OrderedPlanDetails {
  planDetails: string;
  subscriptionPreview: SubscriptionPreview;
}

interface OrderedPlanProps {
  planName: string;
  orderedPlanDetails: Array<OrderedPlanDetails>;
}

const OrderedPlan: React.FC<OrderedPlanProps> = ({
  planName,
  orderedPlanDetails,
}) => (
  <div>
    <Typography
      className="text-high-emphasis"
      disableSpacing
      variant="heading-5"
    >
      {planName}
    </Typography>
    {orderedPlanDetails.map(({ planDetails, subscriptionPreview }) => (
      <div
        key={planDetails}
        className="flex flex-row justify-between mb-1"
        data-qa={planDetails}
      >
        <Typography
          className="text-high-emphasis"
          disableSpacing
          variant="body"
        >
          {planDetails}
        </Typography>
        <Typography
          className="text-high-emphasis font-semibold mb-1"
          disableSpacing
          variant="body"
        >
          {`${amountSeparator(subscriptionPreview.periodRate)}/${subscriptionPreview.interval === PLAN_INTERVAL.YEAR ? 'yr' : 'mo'}`}
        </Typography>
      </div>
    ))}
    <OrderSummarySeparator />
  </div>
);

interface BasicOrderedPlanProps {
  subscriptionPreview: SubscriptionPreview;
}

const OrderedUsagePlan: React.FC<BasicOrderedPlanProps> = ({
  subscriptionPreview,
}) => (
  <OrderedPlan
    orderedPlanDetails={[({
      subscriptionPreview,
      planDetails: `${formatThousand(subscriptionPreview.productAmount, 'standard')} credits/${subscriptionPreview.interval === PLAN_INTERVAL.YEAR ? 'yr' : 'mo'}`,
    })]}
    planName={TESTING_TYPE_TO_LABEL[TESTING_TYPES.USAGE]}
  />
);

interface OrderedAutomatedPlusLivePlanProps {
  vdcSubscriptionPreview: SubscriptionPreview;
  rdcSubscriptionPreview: SubscriptionPreview;
}

const OrderedAutomatedPlusLivePlan: React.FC<OrderedAutomatedPlusLivePlanProps> = ({
  vdcSubscriptionPreview,
  rdcSubscriptionPreview,
}) => {
  const vdcQuantity = vdcSubscriptionPreview?.productAmount ?? 0;
  const vdcPlanDetails = vdcSubscriptionPreview ? [({
    subscriptionPreview: vdcSubscriptionPreview,
    planDetails: `${vdcQuantity} virtual ${pluralize('device', vdcQuantity) as string}`,
  })] : [];

  const rdcQuantity = rdcSubscriptionPreview?.productAmount ?? 0;
  const rdcPlanDetails = rdcSubscriptionPreview ? [({
    subscriptionPreview: rdcSubscriptionPreview,
    planDetails: `${rdcQuantity} real ${pluralize('device', rdcQuantity) as string}`,
  })] : [];

  return (
    <OrderedPlan
      orderedPlanDetails={vdcPlanDetails.concat(rdcPlanDetails)}
      planName="Automated + Live Testing"
    />
  );
};

const OrderedLivePlan: React.FC<BasicOrderedPlanProps> = ({
  subscriptionPreview,
}) => {
  const quantity = subscriptionPreview.productAmount;
  return (
    <OrderedPlan
      orderedPlanDetails={[({
        subscriptionPreview,
        planDetails: `${quantity} parallel ${pluralize('test', quantity) as string}`,
      })]}
      planName="Live Testing"
    />
  );
};

interface OrderedAPITestingPlanProps {
  allPlans: Array<Plan>;
  orderItems: Map<string, PlanConfiguration>;
  subscriptionPreview: SubscriptionPreview;
}

const OrderedAPITestingPlan: React.FC<OrderedAPITestingPlanProps> = ({
  allPlans,
  orderItems,
  subscriptionPreview,
}) => {
  const quantity = subscriptionPreview.productAmount;
  const getPlan = (planType: string) => (
    allPlans.find((plan) => plan.plan_code === `${planType !== PRODUCT_USAGE ? 'unified-' : ''}${planType}-${orderItems.get(planType).interval === PLAN_INTERVAL.MONTH ? PLAN_CODE_INTERNVAL.MONTHLY : PLAN_CODE_INTERNVAL.ANNUAL}`) as Plan
  );

  return (
    <OrderedPlan
      orderedPlanDetails={[({
        subscriptionPreview,
        planDetails: `${quantity} projects, ${formatThousand(projectsToMonthlyExecutions(quantity, getPlan(PRODUCT_API_TESTING).add_ons))} executions/mo`,
      })]}
      planName={TESTING_TYPE_TO_LABEL[TESTING_TYPES.API_TESTING]}
    />
  );
};

const OrderedMobileQualityPlan: React.FC<BasicOrderedPlanProps> = ({
  subscriptionPreview,
}) => (
  <OrderedPlan
    orderedPlanDetails={[({
      subscriptionPreview,
      planDetails: 'Startup',
    })]}
    planName={TESTING_TYPE_TO_LABEL[TESTING_TYPES.MOBILE_QUALITY]}
  />
);

interface OrderedPlansProps {
  allPlans: Array<Plan>;
  orderItems: Map<string, PlanConfiguration>;
  subscriptionPreviews: Array<SubscriptionPreview>;
}

export const OrderedPlans: React.FC<OrderedPlansProps> = ({
  allPlans,
  orderItems,
  subscriptionPreviews,
}) => {
  const productTypes = orderItems.keySeq().toArray();

  return (
    <>
      <div className="mt-2 mb-2">
        {productTypes.includes(PRODUCT_USAGE) && (
          <OrderedUsagePlan
            subscriptionPreview={subscriptionPreviews[PRODUCT_USAGE]}
          />
        )}
      </div>
      <div className="mt-2 mb-2">
        {(productTypes.includes(PRODUCT_VDC) || productTypes.includes(PRODUCT_RDC)) && (
          <OrderedAutomatedPlusLivePlan
            rdcSubscriptionPreview={productTypes.includes(PRODUCT_RDC) ?
              subscriptionPreviews[PRODUCT_RDC] : undefined}
            vdcSubscriptionPreview={productTypes.includes(PRODUCT_VDC) ?
              subscriptionPreviews[PRODUCT_VDC] : undefined}
          />
        )}
      </div>
      <div className="mt-2 mb-2">
        {productTypes.includes(PRODUCT_LIVE) && (
          <OrderedLivePlan
            subscriptionPreview={subscriptionPreviews[PRODUCT_LIVE]}
          />
        )}
      </div>
      <div className="mt-2 mb-2">
        {productTypes.includes(PRODUCT_API_TESTING) && (
          <OrderedAPITestingPlan
            allPlans={allPlans}
            orderItems={orderItems}
            subscriptionPreview={subscriptionPreviews[PRODUCT_API_TESTING]}
          />
        )}
      </div>
      <div className="mt-2 mb-2">
        {productTypes.includes(PRODUCT_MOBILE_QUALITY) && (
          <OrderedMobileQualityPlan
            subscriptionPreview={subscriptionPreviews[PRODUCT_MOBILE_QUALITY]}
          />
        )}
      </div>
    </>
  );
};
